import {FC, PropsWithChildren, ReactNode} from 'react'

import {MAIN_CONTENT_ID, SkipToContentLink} from '@/components/common/content/SkipToContentLink'
import {DebugModal} from '@/components/common/modal/DebugModal'
import {FaqCategories} from '@/content/FAQs'
import {ResourceSubmenuItem} from '@/content/header'
import {HEADER_HEIGHT_DESKTOP, HEADER_HEIGHT_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {usePageTracking} from '@/utils/hooks/usePageTracking'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Footer} from './footer/Footer'
import {HeaderMainButtonProps} from './header/HeaderMainButton'
import {MainHeader} from './header/MainHeader'
import {HelpCenterLazy} from './help-center/HelpCenterLazy'

type PageProps = {
  header?: ReactNode
  hasGetHelpButton?: boolean
  hasFooter?: boolean
  hasRegionalSettings?: boolean
  navigationLabel?: string
  navigationBadge?: ReactNode
  backgroundColor?: string
  hasLogin?: boolean
  resourcesSubmenuItems?: ResourceSubmenuItem[]
  helpCenterCategories?: FaqCategories
  mainCtaButton?: HeaderMainButtonProps
  hasPageOffset?: boolean
}

export const Page: FC<PropsWithChildren<PageProps>> = ({
  header,
  hasGetHelpButton = true,
  hasFooter = true,
  hasRegionalSettings = true,
  navigationLabel,
  navigationBadge,
  backgroundColor = theme.palette.background.default,
  hasLogin = true,
  resourcesSubmenuItems,
  helpCenterCategories,
  mainCtaButton,
  hasPageOffset = true,
  children,
}) => {
  usePageTracking()

  return (
    <>
      <SkipToContentLink />
      {header ? (
        header
      ) : (
        <MainHeader
          hasGetHelpButton={hasGetHelpButton}
          hasRegionalSettings={hasRegionalSettings}
          navigationLabel={navigationLabel}
          navigationBadge={navigationBadge}
          hasLogin={hasLogin}
          resourcesSubmenuItems={resourcesSubmenuItems}
          mainCtaButton={mainCtaButton}
        />
      )}
      <main id={MAIN_CONTENT_ID} tabIndex={-1}>
        {children}
        <HelpCenterLazy sortedCategories={helpCenterCategories} />
      </main>
      {hasFooter ? <Footer /> : hasPageOffset ? <div className="page-offset" /> : null}
      <DebugModal />

      <style jsx>{`
        main {
          /* The desktop header is sticky, so jumping here would otherwise slide main under it */
          scroll-margin-top: ${HEADER_HEIGHT_DESKTOP};
        }
        /* Focused only as the skip link target, not something to interact with */
        main:focus {
          outline: none;
        }
        @media ${TABLET_SCREENSIZE} {
          main {
            scroll-margin-top: 0;
          }
          .page-offset {
            padding-bottom: ${HEADER_HEIGHT_MOBILE};
          }
        }
      `}</style>

      <style jsx global>{`
        body {
          background-color: ${backgroundColor};
        }

        @media ${TABLET_SCREENSIZE} {
          body #missive-webchat,
          body #missive-chat-launcher {
            bottom: calc(${HEADER_HEIGHT_MOBILE} + ${theme.spacing[2]}) !important;
            right: ${theme.spacing[3]} !important;
          }
        }
      `}</style>
    </>
  )
}
